import { loadOrganizationMemberAuthUserIds } from "./membershipHelpers";
import { DATA_SCOPE, getEffectiveScope, isAdmin, isManager } from "./permissions";

/**
 * Resolve the manager's organization_members.id in the actor's current org.
 */
async function loadManagerMembershipId(
  supabaseClient,
  managerSupabaseUserId,
  organizationId,
) {
  if (!supabaseClient || !managerSupabaseUserId || !organizationId) {
    return null;
  }

  const { data: profile, error: profileError } = await supabaseClient
    .from("profiles")
    .select("id")
    .eq("supabase_user_id", managerSupabaseUserId)
    .maybeSingle();

  if (profileError) {
    throw profileError;
  }

  if (!profile?.id) {
    return null;
  }

  const { data: membership, error: membershipError } = await supabaseClient
    .from("organization_members")
    .select("id")
    .eq("organization_id", organizationId)
    .eq("profile_id", profile.id)
    .eq("active", true)
    .maybeSingle();

  if (membershipError) {
    throw membershipError;
  }

  return membership?.id || null;
}

/**
 * Load supabase_user_id values for direct reports of a manager.
 * Prefers organization_members.manager_member_id within the actor org (SoT).
 * Falls back to users.manager_supabase_user_id intersected with org members.
 * RG-06: when an actor is supplied without organization_id, fail closed.
 */
export async function loadDirectReportUserIds(
  supabaseClient,
  managerSupabaseUserId,
  actor = null,
) {
  if (!supabaseClient || !managerSupabaseUserId) {
    return [];
  }

  // Actor present but unscoped → deny (do not omit org membership filter).
  if (actor && !actor.organization_id) {
    return [];
  }

  if (actor?.organization_id) {
    const managerMemberId = await loadManagerMembershipId(
      supabaseClient,
      managerSupabaseUserId,
      actor.organization_id,
    );

    if (managerMemberId) {
      const { data: reportMemberships, error: reportError } =
        await supabaseClient
          .from("organization_members")
          .select("profiles!inner(supabase_user_id)")
          .eq("organization_id", actor.organization_id)
          .eq("manager_member_id", managerMemberId)
          .eq("active", true);

      if (reportError) {
        throw reportError;
      }

      const membershipReportIds = (reportMemberships || [])
        .map((row) => row.profiles?.supabase_user_id)
        .filter(Boolean);

      if (membershipReportIds.length) {
        return membershipReportIds;
      }
    }

    // Compatibility fallback while manager_member_id is still backfilling.
    const orgUserIds = await loadOrganizationMemberAuthUserIds(
      actor.organization_id,
      supabaseClient,
    );

    if (!orgUserIds.length) {
      return [];
    }

    const { data, error } = await supabaseClient
      .from("users")
      .select("supabase_user_id")
      .eq("manager_supabase_user_id", managerSupabaseUserId)
      .in("supabase_user_id", orgUserIds);

    if (error) {
      throw error;
    }

    return (data || []).map((row) => row.supabase_user_id).filter(Boolean);
  }

  // No actor org context — legacy path (should be rare; prefer fail-closed callers).
  const { data, error } = await supabaseClient
    .from("users")
    .select("supabase_user_id")
    .eq("manager_supabase_user_id", managerSupabaseUserId);

  if (error) {
    throw error;
  }

  return (data || []).map((row) => row.supabase_user_id).filter(Boolean);
}

/**
 * Build the list of user IDs visible under a data-scope capability.
 * Returns null when the scope is organization-wide (no user filter) — only when
 * the actor has a positive organization_id (RG-06). Callers must still apply
 * organization_id filters on Gate 4 tables.
 */
export function resolveScopedUserIds(actor, capability, context = {}) {
  const scope = getEffectiveScope(actor, capability, context);
  const actorId = actor?.supabase_user_id;

  if (!actorId || !scope) {
    return [];
  }

  if (scope === DATA_SCOPE.ORGANIZATION) {
    if (!actor?.organization_id) {
      return [];
    }
    return null;
  }

  if (scope === DATA_SCOPE.TEAM) {
    const directReportIds = context.directReportIds || [];
    return [actorId, ...directReportIds].filter(Boolean);
  }

  return [actorId];
}

/**
 * Whether a target user id is within the actor's team (self + direct reports).
 */
export function isUserInTeam(actor, targetUserId, directReportIds = []) {
  if (!actor?.supabase_user_id || !targetUserId) {
    return false;
  }

  if (actor.supabase_user_id === targetUserId) {
    return true;
  }

  if (isAdmin(actor)) {
    return true;
  }

  if (isManager(actor)) {
    return directReportIds.includes(targetUserId);
  }

  return false;
}

/**
 * Whether a project row is visible to the actor under org membership (V1).
 * Project visibility is organization-scoped — not created_by / manager hierarchy.
 * Financial columns remain gated by RLS + operational projections (see
 * docs/engineering/operational_projections.md).
 *
 * `directReportIds` is retained for call-site compatibility; unused for projects.
 */
export function isProjectAccessibleToActor(actor, project, _directReportIds = []) {
  if (!actor || !project) {
    return false;
  }

  const actorOrgId = actor.organization_id;
  if (!actorOrgId) {
    return false;
  }

  // Aggregates / list cards may omit organization_id when already org-scoped upstream.
  if (project.organization_id == null || project.organization_id === "") {
    return true;
  }

  return project.organization_id === actorOrgId;
}

/**
 * Apply project visibility filters to a Supabase query (pre-await).
 * Organization scope is applied by operationalQueryAdapters / orgQueryScope;
 * this no longer filters by created_by. Actors without an organization are denied.
 */
export function applyProjectVisibilityFilter(
  query,
  actor,
  _directReportIds = [],
) {
  if (!actor?.organization_id) {
    return query.eq("project_id", "00000000-0000-0000-0000-000000000000");
  }

  return query;
}
